import React, { useId, useMemo, useState } from 'react';
import {
  addDays,
  addToMyCalendarHref,
  filterScheduleEvents,
  formatEventDate,
  formatEventTimeRange,
  groupEventsByProximity,
  localDayKey,
  mapsUrlForLocation,
  startOfWeekSunday,
  teamLabelForEvent,
  typeBoolsFromShowFilter,
} from '../utils/teamSchedule';
import './TeamScheduleHighlights.css';

const GROUP_META = [
  ['today', 'Today'],
  ['tomorrow', 'Tomorrow'],
  ['thisWeek', 'This Week'],
  ['nextWeek', 'Next Week'],
  ['later', 'Later'],
];

const VIEW_OPTIONS = [
  { id: 'grouped', label: 'Highlights' },
  { id: 'list', label: 'List' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

const TEAM_OPTIONS = [
  { id: 'all', label: 'Both teams (12U & 14U)' },
  { id: '12U', label: '12U only' },
  { id: '14U', label: '14U only' },
];

const SHOW_OPTIONS = [
  { value: 'all', label: 'All event types' },
  { value: 'practice', label: 'Practice & dryland' },
  { value: 'games', label: 'Games & tournaments' },
  { value: 'events', label: 'Other events' },
  { value: 'practice_games', label: 'Practice + games' },
  { value: 'practice_events', label: 'Practice + other events' },
  { value: 'games_events', label: 'Games + other events' },
];

function typeClass(type = '') {
  const base = String(type).toLowerCase().replace(/\s+/g, '-');
  if (!base) return 'type-event';
  return `type-${base}`;
}

function EventCard({ event, compact, showTeamBadge }) {
  const mapsUrl = mapsUrlForLocation(event.location);
  const calendarUrl = addToMyCalendarHref(event);
  const notes = (event.description || '').trim();
  const shortNotes = notes.length > 160 ? `${notes.slice(0, 160)}...` : notes;
  const team = teamLabelForEvent(event);

  return (
    <article className={`team-schedule-card${compact ? ' compact' : ''}`}>
      <div className="team-schedule-card-top">
        {showTeamBadge && team ? (
          <span className="team-schedule-team-pill" title="Team roster">
            {team}
          </span>
        ) : null}
        <span className={`team-schedule-type-pill ${typeClass(event.type)}`}>{event.type || 'Event'}</span>
      </div>
      <h4 className="team-schedule-title">
        {event.htmlLink ? (
          <a href={event.htmlLink} target="_blank" rel="noopener noreferrer">
            {event.title}
          </a>
        ) : (
          event.title
        )}
      </h4>
      <p className="team-schedule-meta">
        <span>{formatEventDate(event.startIso)}</span>
        <span>{formatEventTimeRange(event)}</span>
      </p>
      {event.location ? (
        <p className="team-schedule-location">
          {mapsUrl ? (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              {event.location}
            </a>
          ) : (
            event.location
          )}
        </p>
      ) : null}
      {shortNotes ? <p className="team-schedule-notes">{shortNotes}</p> : null}
      {calendarUrl ? (
        <p className="team-schedule-actions">
          <a href={calendarUrl} target="_blank" rel="noopener noreferrer">
            Add to my calendar
          </a>
        </p>
      ) : null}
    </article>
  );
}

function ScheduleToolbar({
  compact,
  viewMode,
  setViewMode,
  teamFilter,
  setTeamFilter,
  showFilter,
  setShowFilter,
}) {
  const baseId = useId();
  const selectClass = `team-schedule-select${compact ? ' team-schedule-select--compact' : ''}`;

  return (
    <div className={`team-schedule-toolbar${compact ? ' team-schedule-toolbar--compact' : ''}`}>
      <div className="team-schedule-toolbar-main">
        <div className="team-schedule-toolbar-field">
          <label htmlFor={`${baseId}-view`} className="team-schedule-toolbar-label">
            View
          </label>
          <select
            id={`${baseId}-view`}
            className={selectClass}
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            aria-label="Schedule view"
          >
            {VIEW_OPTIONS.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
        <div className="team-schedule-toolbar-field">
          <label htmlFor={`${baseId}-team`} className="team-schedule-toolbar-label">
            Team
          </label>
          <select
            id={`${baseId}-team`}
            className={selectClass}
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            aria-label="Team filter"
          >
            {TEAM_OPTIONS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="team-schedule-toolbar-field team-schedule-toolbar-field--grow">
          <label htmlFor={`${baseId}-show`} className="team-schedule-toolbar-label">
            Show
          </label>
          <select
            id={`${baseId}-show`}
            className={selectClass}
            value={showFilter}
            onChange={(e) => setShowFilter(e.target.value)}
            aria-label="Event types to show"
          >
            {SHOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="team-schedule-toolbar-summary" aria-live="polite">
        Showing:{' '}
        <strong>
          {VIEW_OPTIONS.find((v) => v.id === viewMode)?.label},{' '}
          {TEAM_OPTIONS.find((t) => t.id === teamFilter)?.label},{' '}
          {SHOW_OPTIONS.find((s) => s.value === showFilter)?.label}
        </strong>
      </p>
    </div>
  );
}

function WeekView({ events, compact, weekOffset, setWeekOffset }) {
  const anchor = useMemo(() => {
    const now = new Date();
    return addDays(startOfWeekSunday(now), weekOffset * 7);
  }, [weekOffset]);

  const weekEnd = useMemo(() => addDays(anchor, 6), [anchor]);
  const label = useMemo(() => {
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${anchor.toLocaleDateString(undefined, opts)} – ${weekEnd.toLocaleDateString(undefined, opts)}`;
  }, [anchor, weekEnd]);

  const inWeek = useMemo(
    () =>
      events.filter((ev) => {
        const t = new Date(ev.startIso).getTime();
        const a = anchor.getTime();
        const b = addDays(anchor, 7).getTime();
        return t >= a && t < b;
      }),
    [events, anchor]
  );

  const byDay = useMemo(() => {
    const m = new Map();
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(anchor, i);
      m.set(localDayKey(d), []);
    }
    inWeek.forEach((ev) => {
      const k = localDayKey(ev.startIso);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(ev);
    });
    return m;
  }, [inWeek, anchor]);

  return (
    <div className="team-schedule-week">
      <div className="team-schedule-week-nav">
        <button type="button" className="parents-button team-schedule-nav-btn" onClick={() => setWeekOffset((w) => w - 1)}>
          Previous week
        </button>
        <h3 className="team-schedule-week-title">{label}</h3>
        <button type="button" className="parents-button team-schedule-nav-btn" onClick={() => setWeekOffset((w) => w + 1)}>
          Next week
        </button>
        <button type="button" className="parents-button team-schedule-nav-btn team-schedule-nav-btn--ghost" onClick={() => setWeekOffset(0)}>
          This week
        </button>
      </div>
      <div className="team-schedule-week-columns">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => {
          const d = addDays(anchor, i);
          const key = localDayKey(d);
          const dayEvents = byDay.get(key) || [];
          const isToday = localDayKey(new Date()) === key;
          return (
            <div className={`team-schedule-week-day${isToday ? ' is-today' : ''}`} key={key}>
              <div className="team-schedule-week-day-head">
                <span className="team-schedule-week-dow">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                <span className="team-schedule-week-dom">{d.getDate()}</span>
              </div>
              <div className="team-schedule-week-day-events">
                {dayEvents.length === 0 ? (
                  <p className="team-schedule-week-empty">—</p>
                ) : (
                  dayEvents.map((ev) => <EventCard key={ev.id} event={ev} compact={compact} showTeamBadge />)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ events, compact, monthCursor, setMonthCursor, selectedDayKey, setSelectedDayKey }) {
  const year = monthCursor.getFullYear();
  const month = monthCursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const gridStart = startOfWeekSunday(firstOfMonth);
  const monthName = firstOfMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  const eventsByDay = useMemo(() => {
    const m = new Map();
    events.forEach((ev) => {
      const k = localDayKey(ev.startIso);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(ev);
    });
    return m;
  }, [events]);

  const cells = useMemo(() => {
    const out = [];
    let d = new Date(gridStart);
    for (let i = 0; i < 42; i += 1) {
      out.push(new Date(d));
      d = addDays(d, 1);
    }
    return out;
  }, [gridStart]);

  const selectedEvents = selectedDayKey ? eventsByDay.get(selectedDayKey) || [] : [];

  return (
    <div className="team-schedule-month">
      <div className="team-schedule-month-nav">
        <button
          type="button"
          className="parents-button team-schedule-nav-btn"
          onClick={() => setMonthCursor(new Date(year, month - 1, 1))}
        >
          Previous month
        </button>
        <h3 className="team-schedule-month-title">{monthName}</h3>
        <button
          type="button"
          className="parents-button team-schedule-nav-btn"
          onClick={() => setMonthCursor(new Date(year, month + 1, 1))}
        >
          Next month
        </button>
        <button
          type="button"
          className="parents-button team-schedule-nav-btn team-schedule-nav-btn--ghost"
          onClick={() => setMonthCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
        >
          This month
        </button>
      </div>
      <div className="team-schedule-month-grid" role="grid" aria-label="Month calendar">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((h) => (
          <div key={h} className="team-schedule-month-dow" role="columnheader">
            {h}
          </div>
        ))}
        {cells.map((cell) => {
          const key = localDayKey(cell);
          const inMonth = cell.getMonth() === month;
          const count = (eventsByDay.get(key) || []).length;
          const isToday = localDayKey(new Date()) === key;
          const isSelected = selectedDayKey === key;
          return (
            <button
              key={`${key}-${cell.getTime()}`}
              type="button"
              className={`team-schedule-month-cell${!inMonth ? ' is-outside' : ''}${isToday ? ' is-today' : ''}${
                isSelected ? ' is-selected' : ''
              }`}
              onClick={() => setSelectedDayKey(isSelected ? null : key)}
            >
              <span className="team-schedule-month-dom">{cell.getDate()}</span>
              {count > 0 ? <span className="team-schedule-month-dot" aria-label={`${count} events`} /> : null}
            </button>
          );
        })}
      </div>
      {selectedDayKey ? (
        <div className="team-schedule-month-selected">
          <h4 className="team-schedule-month-selected-title">
            {new Date(`${selectedDayKey}T12:00:00`).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </h4>
          {selectedEvents.length === 0 ? (
            <p className="team-schedule-status">No events.</p>
          ) : (
            <div className="team-schedule-grid">
              {selectedEvents.map((ev) => (
                <EventCard key={ev.id} event={ev} compact={compact} showTeamBadge />
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="team-schedule-month-pick-hint">Tap a day with a dot to see that day&apos;s events.</p>
      )}
    </div>
  );
}

export default function TeamScheduleHighlights({
  events,
  loading,
  error,
  onRetry,
  compact = false,
  showFallbackHint = false,
  showToolbar = true,
}) {
  const [viewMode, setViewMode] = useState('grouped');
  const [teamFilter, setTeamFilter] = useState('all');
  const [showFilter, setShowFilter] = useState('all');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthCursor, setMonthCursor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedDayKey, setSelectedDayKey] = useState(null);

  const typeBools = useMemo(() => typeBoolsFromShowFilter(showFilter), [showFilter]);

  const filtered = useMemo(
    () =>
      filterScheduleEvents(events || [], {
        teamFilter,
        ...typeBools,
      }),
    [events, teamFilter, typeBools]
  );

  const groups = useMemo(() => groupEventsByProximity(filtered), [filtered]);

  const listSorted = useMemo(
    () => [...filtered].sort((a, b) => new Date(a.startIso) - new Date(b.startIso)),
    [filtered]
  );

  if (loading) {
    return <p className="team-schedule-status">Loading upcoming events...</p>;
  }
  if (error) {
    return (
      <div className="team-schedule-status-wrap">
        <p className="team-schedule-status team-schedule-status-error">{error}</p>
        {showFallbackHint ? <p className="team-schedule-status-hint">Try again in a moment.</p> : null}
        {onRetry ? (
          <button type="button" className="parents-button" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (!events || events.length === 0) {
    return <p className="team-schedule-status">Nothing on the schedule for the next few weeks.</p>;
  }

  const emptyAfterFilter = filtered.length === 0;

  return (
    <div className={`team-schedule-explorer${compact ? ' team-schedule-explorer--compact' : ''}`}>
      {showToolbar ? (
        <ScheduleToolbar
          compact={compact}
          viewMode={viewMode}
          setViewMode={setViewMode}
          teamFilter={teamFilter}
          setTeamFilter={setTeamFilter}
          showFilter={showFilter}
          setShowFilter={setShowFilter}
        />
      ) : null}

      {emptyAfterFilter ? (
        <p className="team-schedule-status">No events match your filters. Try selecting another team or event type.</p>
      ) : viewMode === 'grouped' ? (
        <div className={`team-schedule-groups${compact ? ' compact' : ''}`}>
          {GROUP_META.map(([key, label]) =>
            groups[key]?.length ? (
              <section className="team-schedule-group" key={key}>
                <h3 className="team-schedule-group-title">{label}</h3>
                <div className="team-schedule-grid">
                  {groups[key].map((event) => (
                    <EventCard key={event.id} event={event} compact={compact} showTeamBadge />
                  ))}
                </div>
              </section>
            ) : null
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="team-schedule-list-wrap">
          <table className="team-schedule-table event-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Team</th>
                <th>Type</th>
                <th>Title</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {listSorted.map((ev) => {
                const mapsUrl = mapsUrlForLocation(ev.location);
                return (
                  <tr key={ev.id}>
                    <td>{formatEventDate(ev.startIso)}</td>
                    <td>{formatEventTimeRange(ev)}</td>
                    <td>{teamLabelForEvent(ev) || '—'}</td>
                    <td>
                      <span className={`team-schedule-type-pill ${typeClass(ev.type)}`}>{ev.type || 'Event'}</span>
                    </td>
                    <td>
                      {ev.htmlLink ? (
                        <a href={ev.htmlLink} target="_blank" rel="noopener noreferrer">
                          {ev.title}
                        </a>
                      ) : (
                        ev.title
                      )}
                    </td>
                    <td>
                      {ev.location ? (
                        mapsUrl ? (
                          <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                            {ev.location}
                          </a>
                        ) : (
                          ev.location
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : viewMode === 'week' ? (
        <WeekView events={filtered} compact={compact} weekOffset={weekOffset} setWeekOffset={setWeekOffset} />
      ) : (
        <MonthView
          events={filtered}
          compact={compact}
          monthCursor={monthCursor}
          setMonthCursor={setMonthCursor}
          selectedDayKey={selectedDayKey}
          setSelectedDayKey={setSelectedDayKey}
        />
      )}
    </div>
  );
}
